import { useEffect, useMemo, useRef, useState } from 'react';
import Field from '../components/Field.jsx';
import { hasAnswer, isVisible, visibleFields } from '../demo/catalog.js';
import { whoAmI } from '../lib/whoami.js';
import { splitLinks } from '../lib/linkify.js';

/* ONE QUESTION AT A TIME.
 *
 * Ryan, 2026-09-11, looking at the Claude seat form: *"There should only be one
 * question shown at a time. Not even the When do you need it and Anything else
 * that would help questions should be shown yet."*
 *
 * A single page of fourteen questions is the wall the conversational bot was
 * built to avoid, and putting them all on one screen quietly brought it back.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A SEPARATE COMPONENT AND NOT A MODE INSIDE <Form>.
 *
 * Every other request type is short - three to five questions - and Grant's
 * walkthrough scripts under docs/walkthroughs/ are written against the
 * single-page layout, step by step. Turning those into a wizard would rewrite
 * his scripts as a side effect. So the catalogue carries `flow: 'stepper'` and
 * exactly one type sets it.
 * ---------------------------------------------------------------------------
 *
 * VISIBILITY IS RECOMPUTED EVERY RENDER, never cached. showIf rules mean the
 * step list grows and shrinks as answers land, so the current step is tracked
 * by its FIELD KEY rather than by an index - an index silently points at a
 * different question the moment a rule adds one above it.
 */

/* A `warning` is not a question, so it must not be its own screen with a Next
 * button under it. Each one rides along with the question above it, which is
 * the question that triggered it - so it appears the instant the box is ticked.
 */
const AUTO_ADVANCE_MS = 320;

function buildSteps(shown) {
  const steps = [];
  for (const f of shown) {
    if (f.type === 'warning') {
      // A warning declared before any question would have nowhere to attach.
      // None exist, and app/test/catalog.test.js holds that line.
      if (steps.length) steps[steps.length - 1].notes.push(f);
      continue;
    }
    steps.push({ field: f, notes: [] });
  }
  return steps;
}

export default function FormWizard({
  type, fields, answers, setAnswers, submitting, submitError, onSubmit, onBack,
}) {
  const intro = type.intro;
  const [introDone, setIntroDone] = useState(!intro);
  const [stepKey, setStepKey] = useState(null);
  const [showError, setShowError] = useState(false);
  // Which way the screen should move. Purely cosmetic, and reset after each
  // move so a re-render for any other reason does not re-animate.
  const [dir, setDir] = useState('fwd');
  const headingRef = useRef(null);
  const advanceRef = useRef(null);

  /* AUTO-ADVANCE, on single-choice questions only.
   *
   * There are eleven screens. Making every one of them cost a tap plus a Next
   * is twenty-two taps for a one-minute form, and it is the thing that makes a
   * stepper feel slower than the wall of questions it replaced.
   *
   * Only `choice` advances - picking one option IS the whole answer. A multi,
   * a text box and the acknowledgement all wait for Next, because the person is
   * not finished until they say so.
   *
   * The pause is long enough to SEE the pill fill in. Any shorter and the
   * screen changes before the eye confirms what was picked, which reads as a
   * mis-tap even when it was not. */
  const clearAdvance = () => {
    if (advanceRef.current) clearTimeout(advanceRef.current);
    advanceRef.current = null;
  };
  useEffect(() => clearAdvance, []);

  const shown = visibleFields(fields, answers);
  const steps = useMemo(() => buildSteps(shown), [shown]);

  /* THE AUTO-ADVANCE FIRES 320ms AFTER THE CLICK, AND `go` MUST NOT READ THE
   * LIST AS IT WAS AT CLICK TIME.
   *
   * An answer can REVEAL a question - "yes" to branding reveals the Copilot
   * one, "yes" to Snowflake reveals the restricted-data one. The timer closes
   * over the render where the answer had not landed yet, so `steps[index + 1]`
   * there points past the question that has since appeared, and the form walks
   * straight over a question nobody ever sees. Reading the ref instead means
   * `go` always steps through the list as it stands NOW.
   *
   * It went unnoticed because the Snowflake case raises a warning, and a
   * warning suppresses the auto-advance - so that path was always taken by hand
   * one render later, with a fresh list. Branding raises no warning. */
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const rawIndex = steps.findIndex((s) => s.field.key === stepKey);
  const index = rawIndex === -1 ? 0 : rawIndex;
  const step = steps[index];
  const isLast = index === steps.length - 1;

  const answered = step ? hasAnswer(answers[step.field.key]) : false;
  const canAdvance = step ? (!step.field.required || answered) : false;

  /* Move focus to the new question. Without this a keyboard or screen-reader
   * user stays on the Next button they just pressed and never hears the
   * question that replaced it. */
  useEffect(() => {
    if (!introDone) return;
    headingRef.current?.focus();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepKey, introDone]);

  function go(delta) {
    clearAdvance();
    // The live list, not the one this closure was built with - see stepsRef.
    const live = stepsRef.current;
    const at = live.findIndex((s) => s.field.key === stepKey);
    const next = live[(at === -1 ? index : at) + delta];
    if (!next) return;
    setDir(delta > 0 ? 'fwd' : 'back');
    setShowError(false);
    setStepKey(next.field.key);
  }

  function next() {
    clearAdvance();
    if (!canAdvance) {
      setShowError(true);
      return;
    }
    if (isLast) {
      onSubmit();
      return;
    }
    go(1);
  }

  if (intro && !introDone) {
    return <Intro intro={intro} onOk={() => setIntroDone(true)} onCancel={onBack} />;
  }

  if (!step) return null;

  const pct = Math.round(((index + 1) / steps.length) * 100);

  return (
    <div className="wiz">
      <div className="wiz__bar" role="progressbar" aria-valuenow={index + 1} aria-valuemin={1} aria-valuemax={steps.length}>
        <span className="wiz__bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="wiz__meta">
        <span>Question {index + 1} of {steps.length}</span>
        <Who />
      </div>

      {/* Keyed on the field so React replaces the node rather than mutating it,
          which is what lets the entry animation run on every step. */}
      <div className={`wiz__step wiz__step--${dir}`} key={step.field.key}>
        <h2 className="wiz__q" tabIndex={-1} ref={headingRef}>
          {step.field.label}
          {/* BESIDE the question, never under the answers. Under them it reads
              as a note about the option just ticked, which is too late to be
              any use. Derived from the field TYPE, so a multi cannot ship
              without it and nobody has to remember the wording. */}
          {step.field.type === 'multi' ? <span className="wiz__multi">Select all that apply</span> : null}
          {step.field.required ? null : <span className="wiz__opt">optional</span>}
        </h2>
        {step.field.hint ? <p className="wiz__hint">{step.field.hint}</p> : null}

        <Field
          field={{ ...step.field, label: '', hint: '' }}
          value={answers[step.field.key]}
          invalid={showError && step.field.required && !answered}
          onChange={(v) => {
            setShowError(false);
            setAnswers((a) => ({ ...a, [step.field.key]: v }));

            clearAdvance();

            /* AND STOP IF THE ANSWER RAISED A WARNING.
             *
             * Caught by the smoke test, not by eye: answering "yes, I need
             * Snowflake" or "yes, something recurring" makes a red box appear
             * and then slides it off the screen 320ms later. The warning is
             * the most valuable thing on that step - it is the one that can
             * save a new Snowflake account or a whole seat - and auto-advance
             * was carrying people straight past it.
             *
             * Never on the LAST step either: that button says "Send my request
             * to IT", and nothing should press it on somebody's behalf. */
            const raisesWarning = fields.some(
              (f) => f.type === 'warning'
                && f.showIf?.field === step.field.key
                && isVisible(f, { ...answers, [step.field.key]: v }),
            );
            if (step.field.type === 'choice' && hasAnswer(v) && !isLast && !raisesWarning) {
              advanceRef.current = setTimeout(() => go(1), AUTO_ADVANCE_MS);
            }
          }}
        />

        {step.notes.map((nf) => (
          <Field key={nf.key} field={nf} value={undefined} onChange={() => {}} />
        ))}
      </div>

      {showError ? (
        <div className="banner" style={{ marginTop: 12 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>Pick an answer to carry on.</span>
        </div>
      ) : null}

      {submitError ? (
        <div className="banner" style={{ marginTop: 12 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>{submitError}</span>
        </div>
      ) : null}

      <div className="wiz__nav">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => (index === 0 ? (intro ? setIntroDone(false) : onBack()) : go(-1))}
          disabled={submitting}
        >
          <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
          Back
        </button>

        <button
          type="button"
          className="btn btn--primary"
          onClick={next}
          disabled={submitting}
          /* Deliberately NOT disabled when unanswered. A greyed-out button with
             no explanation is the commonest "this form is broken" complaint -
             pressing it says what is missing instead. */
          aria-disabled={!canAdvance}
        >
          {submitting ? 'Sending…' : isLast ? 'Send my request to IT' : 'Next'}
          {submitting || isLast ? null : (
            <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
          )}
        </button>
      </div>

      {/* A send takes a few seconds, and a button reading "Sending..." with
          nothing under it looks like a page that has frozen. Ryan, 2026-09-11:
          *"it took a while and just sat there for a bit."* Saying what is
          happening turns the same wait into a step. */}
      {submitting ? (
        <p className="muted" style={{ fontSize: 12, marginTop: 10 }} aria-live="polite">
          Filing your request and letting IT know. This takes a few seconds - please do not
          close the page.
        </p>
      ) : null}
    </div>
  );
}

/* The screen before the questions. Two panels, in Ryan's order - Kenknee first,
 * then what a paid seat is actually for - and an explicit OK or Cancel, so
 * nobody files a request having skimmed past the cheaper options. */
function Intro({ intro, onOk, onCancel }) {
  return (
    <div className="wiz wiz__step wiz__step--fwd">
      <h2 className="wiz__q" style={{ marginTop: 0 }}>{intro.title}</h2>

      {intro.panels.map((p) => (
        <div key={p.title} className={`introp introp--${p.tone}`}>
          <span className="material-symbols-outlined introp__icon" aria-hidden="true">{p.icon}</span>
          <div>
            <b>{p.title}</b>
            {/* A panel may need more than one paragraph - the options panel
                covers Kenknee and Copilot, which are two different answers. */}
            {[p.body].flat().map((para) => (
              <p key={para}>
                {/* The matching is a pure module so it can be unit-tested;
                    this turns each part into a node. React escapes the text,
                    so no markup can arrive through panel copy. */}
                {splitLinks(para).map((part, i) => (typeof part === 'string' ? part : (
                  <a key={part.href + i} href={part.href} target="_blank" rel="noopener noreferrer">
                    {part.text}
                  </a>
                )))}
              </p>
            ))}
          </div>
        </div>
      ))}

      {intro.footnote ? <p className="wiz__hint" style={{ marginTop: 14 }}>{intro.footnote}</p> : null}

      <div className="wiz__nav">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {intro.cancelLabel || 'Cancel'}
        </button>
        <button type="button" className="btn btn--primary" onClick={onOk}>
          {intro.okLabel || 'OK'}
          <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
    </div>
  );
}

/* WHO IS ASKING - and only what the directory actually knows.
 *
 * This used to read "RHayes - Dispatcher - Beehive Plumbing" for everybody,
 * because the role and the brand came from the PROTOTYPE's demo profile.
 * Ryan, 2026-09-11: *"That is not correct."* Measured against the live tenant,
 * Entra has a mail address for 99% of accounts, a job title for 11% and a
 * department for 0.1% - so a name and an address are all that can be shown
 * honestly, and the title only when there is one.
 *
 * Renders nothing at all until the lookup answers, rather than flashing a guess.
 */
function Who() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    whoAmI().then((who) => { if (!cancelled) setMe(who); });
    return () => { cancelled = true; };
  }, []);

  if (!me?.name) return <span />;
  return (
    <span className="wiz__who" title={me.email || ''}>
      <span className="material-symbols-outlined" aria-hidden="true">account_circle</span>
      {me.name}{me.jobTitle ? ` · ${me.jobTitle}` : ''}
    </span>
  );
}
